import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { motion, AnimateSharedLayout } from "framer-motion";
import { Form, FormSpy } from "react-final-form";
import { Button } from "@blueprintjs/core";
import setFieldData from "final-form-set-field-data";
import setFieldTouched from "final-form-set-field-touched";
import createDecorator from "final-form-focus";
import PageTransition from "../animate/PageTransition";
import { setError, getEstimate } from "../actions/estimate";
import ErrorMessage from "./ErrorMessage";
import { acceptEstimate, getSettings } from "../actions";

const focusOnErrors = createDecorator();
const TruckIcon = "assets/images/truck-icon.png";
const names = ["Your Move", "Your Details", "Estimate", "Book", "Survey"];

class Wizard extends React.Component {
  static Page = ({ children }) => (
    <>
      <ErrorMessage />
      {children}
    </>
  );

  constructor(props) {
    super(props);
    this.state = {
      page: 0,
    };
  }

  componentDidMount() {
    const { fetchSettings } = this.props;
    fetchSettings();
  }

  next = () => {
    const { children } = this.props;
    this.setState((state) => ({
      page: Math.min(state.page + 1, children.length - 1),
    }));
  };

  previous = () => {
    const { clearError } = this.props;

    this.setState((state) => ({
      page: Math.max(state.page - 1, 0),
    }));
    clearError();
  };

  validate = (values) => {
    const { children } = this.props;
    const { page } = this.state;
    const activePage = React.Children.toArray(children)[page];
    return activePage.props.validate ? activePage.props.validate(values) : {};
  };

  handleSubmit = (values) => {
    const { children, fetchEstimate, quoteNumber, handleDone } = this.props;
    const { page } = this.state;
    const isLastPage = page === React.Children.count(children) - 1;

    if ((values.international || values.live_in === "other") && page > 0) {
      // there's an error on the API if moving_to is empty, but moving to is empty if move is international, so I need to remove moving_to before posting

      const { moving_to, ...otherValues } = values;
      return fetchEstimate(otherValues)
        .then((success) => {
          if (success) {
            this.next();
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
    }

    if (values.estimateActions === "contact" && quoteNumber && page === 2) {
      // remove pay_by if user has comes back and decides they'd rather talk to someone
      const { pay_by, ...otherValues } = values;
      return handleDone(
        {
          ...otherValues,
          next_step: "talk",
          survey_method: "phonecall",
        },
        quoteNumber
      )
        .then((success) => {
          if (success) {
            this.next();
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
    }

    if (!isLastPage) {
      this.next();
    }

    return {};
  };

  render() {
    const { children, done, price, error } = this.props;
    const { page } = this.state;
    const activePage = React.Children.toArray(children)[page];
    const isLastPage = page === React.Children.count(children) - 1;

    return (
      <PageTransition page={page}>
        <Form
          initialValues={{
            estimateActions: "book",
            add_packing: false,
            paymenttoken: null,
            date_flexible: true,
          }}
          validate={this.validate}
          onSubmit={this.handleSubmit}
          decorators={[focusOnErrors]}
          mutators={{
            setFieldData,
            setFieldTouched,
            setFieldValue: (args, state, utils) => {
              const [field, value] = args;
              utils.changeValue(state, field, () => value);
            },
            clearMovingTo: (_args, state, utils) => {
              utils.changeValue(state, "moving_to", () => "");
            },
          }}
        >
          {(props) => {
            const { handleSubmit, submitting, pristine, values, validating } =
              props;

            return (
              <form onSubmit={handleSubmit}>
                <div className="header">
                  <div className="header__overlay" />
                  <div className="wrap">
                    <div className="header__title">
                      <h2>Get a quote!</h2>
                    </div>
                    <div className="header__bar">
                      <div className="progress-tracker">
                        <div className="container">
                          <div className="progress-tracker__items">
                            <div className="progress-tracker__block">
                              <div className="progress-tracker__step">
                                Your move
                              </div>
                              <div
                                className={`progress-tracker__point ${
                                  page >= 0 ? "active" : ""
                                }`}
                              />
                            </div>
                            <div className="progress-tracker__block">
                              <div className="progress-tracker__step">
                                Your Details
                              </div>
                              <div
                                className={`progress-tracker__point ${
                                  page >= 1 ? "active" : ""
                                }`}
                              />
                            </div>
                            <div className="progress-tracker__block">
                              <div className="progress-tracker__step">
                                Estimate
                              </div>
                              <div
                                className={`progress-tracker__point ${
                                  page >= 2 ? "active" : ""
                                }`}
                              />
                            </div>
                            <div className="progress-tracker__block">
                              <div className="progress-tracker__step">Book</div>
                              <div
                                className={`progress-tracker__point ${
                                  page >= 3 ? "active" : ""
                                }`}
                              />
                            </div>
                            <div className="progress-tracker__block">
                              <div className="progress-tracker__step">
                                Survey
                              </div>
                              <div
                                className={`progress-tracker__point ${
                                  page === 4 ? "active" : ""
                                }`}
                              />
                            </div>
                          </div>
                          <div className="progress-tracker__pointer">
                            <div className="progress-tracker__icon">
                              <motion.div
                                animate={{ x: `${page * 23}%` }}
                                transition={{ duration: 0.7, delay: 0.1 }}
                              >
                                <img src={TruckIcon} alt="truck" />
                              </motion.div>
                            </div>
                          </div>
                          <div className="progress-tracker__bar-wrap">
                            <div className="progress-tracker__bar" />
                            <motion.div
                              className="progress-tracker__bar progress-tracker__bar--overlay"
                              animate={{ width: `${page * 25 + 6}%` }}
                              transition={{ duration: 0.7 }}
                            />
                          </div>
                          <div className="progress-tracker__mobile">
                            <div className="progress-tracker__mobile-header">
                              {names[page]}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="container">
                  <AnimateSharedLayout>
                    {activePage}
                    <motion.div layout className="button-group">
                      <FormSpy subscription={{ values: true }}>
                        {() => (
                          <>
                            {page > 0 && !done && (
                              <Button
                                minimal
                                className="button"
                                type="button"
                                onClick={this.previous}
                              >
                                Previous
                              </Button>
                            )}
                            {!isLastPage && !done && (
                              <>
                                <span />
                                <Button
                                  type="submit"
                                  className="button --flex-right"
                                  disabled={
                                    (pristine && page !== 2) ||
                                    (!price && page > 1) ||
                                    !!error
                                  }
                                  loading={validating || submitting}
                                >
                                  {(values.international ||
                                    values.live_in === "other") &&
                                  page > 0
                                    ? "Let's go!"
                                    : `Next: ${
                                        (!isLastPage &&
                                          values.estimateActions ===
                                            "contact" &&
                                          page === 2) ||
                                        values.live_in === "other"
                                          ? "Contact"
                                          : names[page + 1]
                                      }`}
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </FormSpy>
                    </motion.div>
                  </AnimateSharedLayout>
                </div>
              </form>
            );
          }}
        </Form>
      </PageTransition>
    );
  }
}

Wizard.propTypes = {
  fetchEstimate: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  done: PropTypes.bool,
  price: PropTypes.number,
  quoteNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleDone: PropTypes.func.isRequired,
  fetchSettings: PropTypes.func.isRequired,
  error: PropTypes.instanceOf(Error),
};

Wizard.defaultProps = {
  done: false,
  price: 0,
  quoteNumber: null,
  error: null,
};

const mapDispatchToProps = (dispatch) => ({
  fetchEstimate: (values) => dispatch(getEstimate(values)),
  clearError: () => dispatch(setError(null)),
  handleDone: (values, quoteNumber) =>
    dispatch(acceptEstimate(values, quoteNumber)),
  fetchSettings: () => dispatch(getSettings()),
});

const mapStateToProps = (state) => ({
  quoteNumber: state.estimate.id,
  price: state.estimate.estimate,
  done: state.done,
  error: state.estimate.error,
});

export default connect(mapStateToProps, mapDispatchToProps)(Wizard);
